// KONTRAK lintas-layar (pemilik: kelompok "panduan"). v1: A.openSearch() di public/js/help.js —
// palet pencarian global (tombol kaca pembesar di topbar, Ctrl/⌘+K, "/"). Shell memasang SearchProvider.
// Server: GET /api/search?q=&limit= (nota, SPO, toko, rute, produk, setoran, pengguna — dibatasi per peran).
// Klien: menu & topik panduan dicari langsung di HP (tanpa server) — juga untuk akun toko.
import type { Role } from '@sj/shared';
import {
  createContext,
  Fragment,
  type KeyboardEvent,
  type ReactNode,
  type SyntheticEvent,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router';
import { DESC, type NavItem, navFor } from '../../app/nav.js';
import { useSession } from '../../app/session.js';
import { Icon, type IconName } from '../../components/Icon.js';
import { useToast } from '../../components/Toast.js';
import { Pill } from '../../components/ui.js';
import { api } from '../../lib/api.js';
import { qs, rp } from '../../lib/format.js';
import type { StatusKind } from '../../lib/status.js';
import { useOpenStore } from '../data/open.js';
import { useOpenNota } from '../nota/open.js';
import { useOpenRoute } from '../rute/open.js';
import { useOpenSpo } from '../spo/open.js';
import { forRole, GLOSS, norm, topicsFor, topicText } from './content.js';

/** Satu hasil pencarian (bentuk item GET /api/search + hasil lokal menu/panduan). */
export interface SearchHit {
  type: string;
  id?: number;
  title: string;
  subtitle?: string;
  icon?: IconName;
  kind?: StatusKind;
  status?: string;
  tag?: string;
  amount?: number | null;
  /** Tautan cadangan (v1 '#/x' — diubah ke '/x'). */
  go?: string;
}
export interface SearchGroup {
  type: string;
  label: string;
  items: SearchHit[];
}

const GICON: Record<string, IconName> = {
  menu: 'list',
  panduan: 'headset',
  nota: 'receipt',
  spo: 'doc',
  toko: 'store',
  rute: 'nav',
  produk: 'tag',
  setoran: 'wallet',
  pengguna: 'users',
};
const reEsc = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
const remoteOK = (role: Role) => ['owner', 'admin', 'sales', 'driver'].includes(role);
const toPath = (go: string) => (go.startsWith('#') ? go.slice(1) : go);

/** Sorot kata kunci dengan <mark> (v1 hl()). */
function Hl({ text, q }: { text: unknown; q: string }): ReactNode {
  const s = String(text ?? '');
  const w = q.trim();
  if (!w) return s;
  return s
    .split(new RegExp(`(${reEsc(w)})`, 'ig'))
    .map((p, i) => (i % 2 ? <mark key={i}>{p}</mark> : p ? <Fragment key={i}>{p}</Fragment> : null));
}

// Riwayat pencarian terakhir per pengguna (localStorage, kunci sama dengan v1).
const recentKey = (uid: number) => `gs.recent.${uid}`;
const recentGet = (uid: number): string[] => {
  try {
    return (JSON.parse(localStorage.getItem(recentKey(uid)) || '[]') as string[]).slice(0, 6);
  } catch {
    return [];
  }
};
const recentAdd = (uid: number, q: string) => {
  try {
    const l = [q, ...recentGet(uid).filter((x) => x !== q)].slice(0, 6);
    localStorage.setItem(recentKey(uid), JSON.stringify(l));
  } catch {
    /* penyimpanan tidak tersedia */
  }
};

/** Menu & panduan yang cocok (dicari di klien). */
function localGroups(q: string, role: Role, nav: NavItem[]): SearchGroup[] {
  const words = norm(q).split(/\s+/).filter(Boolean);
  const hit = (hay: string) => words.every((w) => norm(hay).includes(w));
  const menu: SearchHit[] = nav
    .filter(([k, l]) => !words.length || hit(`${l} ${DESC[k] || ''}`))
    .map(([k, l, i]) => ({ type: 'menu', title: l, subtitle: DESC[k] || '', icon: i, go: `/${k}` }));
  const out: SearchGroup[] = [];
  if (menu.length) out.push({ type: 'menu', label: 'Menu', items: menu.slice(0, words.length ? 6 : 8) });
  if (words.length) {
    const help: SearchHit[] = topicsFor(role)
      .filter((t) => hit(topicText(t)))
      .slice(0, 5)
      .map((t) => ({ type: 'panduan', title: t.title, subtitle: t.summary, icon: t.icon, go: `/panduan?t=${t.id}` }));
    const gl: SearchHit[] = GLOSS.filter((g) => forRole(g, role) && hit(`${g.term} ${g.def}`))
      .slice(0, 3)
      .map((g) => ({ type: 'panduan', title: `Istilah: ${g.term}`, subtitle: g.def, go: '/panduan?t=istilah' }));
    if (help.length || gl.length) out.push({ type: 'panduan', label: 'Panduan', items: [...help, ...gl] });
  }
  return out;
}

interface PaletteHandle {
  set: (q: string) => void;
  focus: () => void;
}

/** Palet pencarian — DOM sama dengan .gs-back v1 (ditambahkan ke #modalRoot). */
function Palette({ q0, onClose, handle }: { q0: string; onClose: () => void; handle: { current: PaletteHandle | null } }) {
  const { me, can } = useSession();
  const navigate = useNavigate();
  const toast = useToast();
  const openNota = useOpenNota();
  const openSpo = useOpenSpo();
  const openRoute = useOpenRoute();
  const openStore = useOpenStore();
  const remote = remoteOK(me.role);
  const [q, setQ] = useState(q0);
  const [res, setRes] = useState<{ groups: SearchGroup[]; q: string; pending: boolean }>(() => ({
    groups: localGroups(q0, me.role, navFor(me.role, can)),
    q: q0,
    pending: remote && q0.trim().length >= 2,
  }));
  const [sel, setSel] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const seq = useRef(0);
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined);
  const alive = useRef(true);

  const flat = useMemo(() => res.groups.flatMap((g) => g.items), [res.groups]);
  const cur = Math.min(sel, Math.max(0, flat.length - 1));

  const run = useCallback(
    (value: string) => {
      const my = ++seq.current;
      const local = localGroups(value, me.role, navFor(me.role, can));
      const want = remote && value.trim().length >= 2;
      setSel(0);
      setRes({ groups: local, q: value, pending: want });
      clearTimeout(timer.current);
      if (!want) return;
      timer.current = setTimeout(async () => {
        let groups: SearchGroup[] = [];
        try {
          groups =
            (await api<{ groups?: SearchGroup[] }>('GET', `/api/search?${qs({ q: value.trim(), limit: 5 })}`)).groups || [];
        } catch {
          groups = [];
        }
        if (my !== seq.current || !alive.current) return; // hasil basi: pengguna sudah mengetik lagi
        // Data (nota, toko, …) lebih dulu, lalu menu & panduan.
        setRes({ groups: [...groups, ...local], q: value, pending: false });
      }, 220);
    },
    [me.role, remote, can],
  );

  const change = useCallback(
    (value: string) => {
      setQ(value);
      run(value);
    },
    [run],
  );

  // Pencarian awal + fokus (v1: run(); setTimeout(focus, 0)).
  // biome-ignore lint/correctness/useExhaustiveDependencies: hanya saat palet dibuka
  useEffect(() => {
    alive.current = true;
    run(q0);
    const f = setTimeout(() => inputRef.current?.focus(), 0);
    return () => {
      alive.current = false;
      clearTimeout(f);
      clearTimeout(timer.current);
    };
  }, []);

  useEffect(() => {
    handle.current = {
      set: (v) => change(v),
      focus: () => inputRef.current?.focus(),
    };
    return () => {
      handle.current = null;
    };
  }, [handle, change]);

  // Baris terpilih terlihat & diumumkan (v1 mark()).
  useLayoutEffect(() => {
    const el = bodyRef.current?.querySelector<HTMLElement>(`#gs-${cur}`);
    if (el) {
      el.scrollIntoView({ block: 'nearest' });
      inputRef.current?.setAttribute('aria-activedescendant', el.id);
    }
  });

  const open = (x: SearchHit) => {
    const v = q.trim();
    if (v.length >= 2) recentAdd(me.id, v);
    onClose();
    const fail = (e: unknown) => toast((e as Error).message, true);
    if (x.type === 'nota' && x.id != null) return void openNota(x.id).catch(fail);
    if (x.type === 'spo' && x.id != null) return void openSpo(x.id).catch(fail);
    if (x.type === 'rute' && x.id != null) return void openRoute(x.id).catch(fail);
    // v1: toko → modal detail toko (A.acts['store-open']) untuk owner/admin/sales.
    if (x.type === 'toko' && x.id != null && ['owner', 'admin', 'sales'].includes(me.role))
      return void openStore(x.id).catch(fail);
    if (x.go) navigate(toPath(x.go));
  };

  const onKey = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (flat.length) setSel((cur + 1) % flat.length);
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (flat.length) setSel((cur - 1 + flat.length) % flat.length);
      return;
    }
    if (e.key === 'Enter' && e.target === inputRef.current) {
      e.preventDefault();
      const x = flat[cur];
      if (x) open(x);
    }
  };

  const rq = res.q;
  const recent = rq.trim() ? [] : recentGet(me.id);
  let i = 0;
  return (
    <div
      className="gs-back"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      onKeyDown={onKey}
    >
      <div className="gs" role="dialog" aria-modal="true" aria-label="Pencarian global">
        <div className="gs-h">
          <Icon n="search" />
          <input
            ref={inputRef}
            className="gs-in"
            type="search"
            enterKeyHint="search"
            placeholder={remote ? 'Cari nota, SPO, toko, rute, produk, menu, panduan…' : 'Cari menu & panduan…'}
            autoComplete="off"
            spellCheck="false"
            aria-label="Kata kunci"
            aria-controls="gsList"
            value={q}
            onChange={(e) => change(e.target.value)}
          />
          <button type="button" className="icon-btn" data-gs="close" aria-label="Tutup" onClick={onClose}>
            <Icon n="x" cls="i-sm" />
          </button>
        </div>
        <div className="gs-b" id="gsList" role="listbox" ref={bodyRef}>
          {recent.length ? (
            <>
              <div className="gs-g">Pencarian terakhir</div>
              <div className="gs-rec">
                {recent.map((x) => (
                  <button
                    key={x}
                    type="button"
                    className="chip"
                    data-gs="recent"
                    data-q={x}
                    onClick={() => {
                      change(x);
                      inputRef.current?.focus();
                    }}
                  >
                    {x}
                  </button>
                ))}
              </div>
            </>
          ) : null}
          {res.groups.map((g) => (
            <Fragment key={`${g.type}-${g.label}`}>
              <div className="gs-g">{g.label}</div>
              {g.items.map((x) => {
                const n = i++;
                const on = n === cur;
                return (
                  <button
                    key={n}
                    type="button"
                    className={`gs-row${on ? ' sel' : ''}`}
                    role="option"
                    data-i={n}
                    id={`gs-${n}`}
                    aria-selected={on ? 'true' : 'false'}
                    onClick={() => open(x)}
                  >
                    <span className="gs-ic">
                      <Icon n={x.icon || GICON[x.type] || 'doc'} cls="i-sm" />
                    </span>
                    <span className="gs-t">
                      <b>
                        <Hl text={x.title} q={rq} />
                      </b>
                      {x.subtitle ? (
                        <span>
                          <Hl text={x.subtitle} q={rq} />
                        </span>
                      ) : null}
                    </span>
                    <span className="gs-e">
                      {x.kind ? (
                        <Pill kind={x.kind} v={x.status} />
                      ) : x.tag ? (
                        <span className="pill t-slate">{x.tag}</span>
                      ) : null}
                      {x.amount != null ? <em>{rp(x.amount)}</em> : null}
                    </span>
                  </button>
                );
              })}
            </Fragment>
          ))}
          {res.pending ? (
            <div className="gs-note">
              <span className="spin" />
              {' Mencari data…'}
            </div>
          ) : rq.trim() && !flat.length ? (
            <div className="gs-note">
              {'Tidak ada hasil untuk "'}
              <b>{rq.trim()}</b>
              {`". ${remote && rq.trim().length < 2 ? 'Ketik minimal 2 huruf.' : 'Coba nomor dokumen atau nama toko.'}`}
            </div>
          ) : null}
        </div>
        <div className="gs-f">
          <span>
            <kbd>↑</kbd>
            <kbd>↓</kbd> pilih
          </span>
          <span>
            <kbd>Enter</kbd> buka
          </span>
          <span>
            <kbd>Esc</kbd> tutup
          </span>
          <span className="grow" />
          <span>{remote ? 'min. 2 huruf' : ''}</span>
        </div>
      </div>
    </div>
  );
}

interface SearchCtx {
  /** Buka palet; q0 = kata kunci awal (v1 A.openSearch(q0), mis. kotak cari di Beranda). */
  open: (q0?: string) => void;
}
const Ctx = createContext<SearchCtx>({ open: () => {} });

const typing = (t: EventTarget | null) =>
  t instanceof HTMLElement && (t.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName));

/** Penyedia palet pencarian (pintasan keyboard dipasang di sini). */
export function SearchProvider({ children }: { children: ReactNode }) {
  const [pal, setPal] = useState<{ key: number; q0: string } | null>(null);
  const handle = useRef<PaletteHandle | null>(null);
  const palOpen = useRef(false);
  palOpen.current = !!pal;
  const seq = useRef(0);

  const open = useCallback((q0 = '') => {
    if (palOpen.current) {
      if (q0) handle.current?.set(q0);
      handle.current?.focus();
      return;
    }
    palOpen.current = true;
    seq.current += 1;
    setPal({ key: seq.current, q0 });
  }, []);
  const close = useCallback(() => {
    palOpen.current = false;
    setPal(null);
  }, []);

  // Pintasan keyboard: Ctrl/⌘+K di mana saja, "/" bila tidak sedang mengetik.
  useEffect(() => {
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (!document.getElementById('app')) return;
      if ((e.ctrlKey || e.metaKey) && !e.shiftKey && !e.altKey && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        open();
        return;
      }
      if (
        e.key === '/' &&
        !e.ctrlKey &&
        !e.metaKey &&
        !typing(e.target) &&
        !palOpen.current &&
        !document.querySelector('.modal-back')
      ) {
        e.preventDefault();
        open();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  const value = useMemo(() => ({ open }), [open]);
  const root = document.getElementById('modalRoot');
  return (
    <Ctx.Provider value={value}>
      {children}
      {pal && root ? createPortal(<Palette key={pal.key} q0={pal.q0} onClose={close} handle={handle} />, root) : null}
    </Ctx.Provider>
  );
}

/**
 * Buka palet pencarian global (v1 A.openSearch(q0)). q = kata kunci awal (mis. kotak cari di Beranda); palet yang
 * sudah terbuka diisi ulang & difokuskan. Selain teks diabaikan — aman dipasang langsung sebagai onClick.
 */
export function useOpenSearch(): OpenSearch {
  const { open } = useContext(Ctx);
  return useCallback((q?: unknown) => open(typeof q === 'string' ? q : ''), [open]);
}

/** (q?: string) => void — ditambah tanda tangan event agar bisa langsung jadi onClick (Shell: onClick={openSearch}). */
export interface OpenSearch {
  (q?: string): void;
  (ev: SyntheticEvent): void;
}
